import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Input } from "@/components/ui/input";
import { Country } from "country-state-city";

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

// --- Country detection helpers (no network, no permissions needed) ---

// IANA timezone prefix/city → ISO 2-letter country code
const TZ_MAP = {
  "Pacific/Auckland": "nz",
  "Pacific/Chatham": "nz",
  "Australia/Sydney": "au",
  "Australia/Melbourne": "au",
  "Australia/Brisbane": "au",
  "Australia/Adelaide": "au",
  "Australia/Perth": "au",
  "Australia/Darwin": "au",
  "Australia/Hobart": "au",
  "Australia/Lord_Howe": "au",
  "America/New_York": "us",
  "America/Chicago": "us",
  "America/Denver": "us",
  "America/Los_Angeles": "us",
  "America/Phoenix": "us",
  "America/Anchorage": "us",
  "America/Honolulu": "us",
  "America/Toronto": "ca",
  "America/Vancouver": "ca",
  "America/Winnipeg": "ca",
  "America/Halifax": "ca",
  "America/St_Johns": "ca",
  "America/Sao_Paulo": "br",
  "America/Argentina/Buenos_Aires": "ar",
  "America/Santiago": "cl",
  "America/Bogota": "co",
  "America/Lima": "pe",
  "America/Mexico_City": "mx",
  "Europe/London": "gb",
  "Europe/Dublin": "ie",
  "Europe/Paris": "fr",
  "Europe/Berlin": "de",
  "Europe/Madrid": "es",
  "Europe/Rome": "it",
  "Europe/Amsterdam": "nl",
  "Europe/Brussels": "be",
  "Europe/Zurich": "ch",
  "Europe/Stockholm": "se",
  "Europe/Oslo": "no",
  "Europe/Copenhagen": "dk",
  "Europe/Helsinki": "fi",
  "Europe/Warsaw": "pl",
  "Europe/Prague": "cz",
  "Europe/Vienna": "at",
  "Europe/Budapest": "hu",
  "Europe/Bucharest": "ro",
  "Europe/Athens": "gr",
  "Europe/Lisbon": "pt",
  "Europe/Moscow": "ru",
  "Asia/Tokyo": "jp",
  "Asia/Seoul": "kr",
  "Asia/Shanghai": "cn",
  "Asia/Hong_Kong": "hk",
  "Asia/Singapore": "sg",
  "Asia/Kolkata": "in",
  "Asia/Colombo": "lk",
  "Asia/Karachi": "pk",
  "Asia/Dubai": "ae",
  "Asia/Riyadh": "sa",
  "Asia/Bangkok": "th",
  "Asia/Kuala_Lumpur": "my",
  "Asia/Manila": "ph",
  "Asia/Jakarta": "id",
  "Asia/Taipei": "tw",
  "Asia/Dhaka": "bd",
  "Asia/Kathmandu": "np",
  "Asia/Tehran": "ir",
  "Asia/Baghdad": "iq",
  "Asia/Jerusalem": "il",
  "Asia/Tbilisi": "ge",
  "Africa/Cairo": "eg",
  "Africa/Johannesburg": "za",
  "Africa/Nairobi": "ke",
  "Africa/Lagos": "ng",
  "Africa/Casablanca": "ma",
};

/**
 * Detect the user's country ISO code (lowercase) from browser signals only.
 * Uses timezone only - locale/language is intentionally skipped because
 * "en-US" is widely used by non-US users and gives false positives.
 * Returns null if detection is inconclusive.
 */
function detectCountryFromBrowser() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const code = TZ_MAP[tz];
    if (code) return code;
  } catch {
    // intentional empty catch
  }
  return null;
}

// --- IP geolocation (most reliable - fires once per session, result cached) ---
// ipapi.co/country/ returns a plain 2-letter ISO code, no auth needed, CORS-open
let _ipCountryCache = null; // null = not yet fetched, "" = failed/unknown, "lk" etc = known
let _ipFetchStarted = false;

function startIPDetection(onResult) {
  if (_ipCountryCache !== null) {
    if (_ipCountryCache) onResult(_ipCountryCache);
    return;
  }
  if (_ipFetchStarted) return;
  _ipFetchStarted = true;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 4000);

  fetch("https://ipapi.co/country/", { signal: controller.signal })
    .then((r) => r.text())
    .then((raw) => {
      clearTimeout(timeout);
      const code = raw.trim().toLowerCase();
      _ipCountryCache = /^[a-z]{2}$/.test(code) ? code : "";
      if (_ipCountryCache) onResult(_ipCountryCache);
    })
    .catch(() => {
      clearTimeout(timeout);
      _ipCountryCache = "";
    });
}

// Singleton - inject the Maps JS script once per page load
let scriptPromise = null;
function loadGoogleMaps() {
  if (!scriptPromise && API_KEY) {
    scriptPromise = new Promise((resolve, reject) => {
      if (window.google?.maps?.places) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Google Maps"));
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * AddressAutocomplete
 *
 * Props:
 *   value         {string}  – controlled value for the address field
 *   onChange      {fn}      – called with the raw string on every keystroke
 *   onPlaceSelect {fn}      – called when user picks a suggestion:
 *                             ({ address, city, state, country, postcode })
 *   countryCode   {string}  – optional ISO-2 override (e.g. "nz"). When omitted
 *                             the component auto-detects from Redux → locale → timezone.
 *   placeholder   {string}
 *   className     {string}
 *   id / name     {string}  – forwarded to the underlying <Input>
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  countryCode: countryCodeProp,
  placeholder = "Start typing an address…",
  className,
  id,
  name,
}) {
  const [googleReady, setGoogleReady] = useState(false);

  // Resolve country code: explicit prop > Redux store country > browser detection
  const storeCountry = useSelector((state) => state.current_store?.country);

  const syncCountryCode =
    countryCodeProp ||
    (storeCountry
      ? Country.getAllCountries()
        .find((c) => c.name === storeCountry)
        ?.isoCode?.toLowerCase()
      : null) ||
    detectCountryFromBrowser();

  // IP-based detection fills in when browser signals are inconclusive
  const [ipCountry, setIpCountry] = useState(_ipCountryCache || null);
  useEffect(() => {
    if (syncCountryCode) return; // already resolved without IP
    startIPDetection((code) => setIpCountry(code));
  }, [syncCountryCode]);

  const resolvedCountryCode = syncCountryCode || ipCountry;

  useEffect(() => {
    if (!API_KEY) return;
    loadGoogleMaps()
      .then(() => setGoogleReady(true))
      .catch((err) => console.error("Google Maps failed to load:", err));
  }, []);

  // No API key or still loading → plain input
  if (!googleReady || !API_KEY) {
    return (
      <Input
        id={id}
        name={name}
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={
          !API_KEY ? placeholder + " (Places API key not set)" : placeholder
        }
        className={className}
      />
    );
  }

  return (
    // key forces PlacesInput to remount when country resolves (e.g. after IP detection)
    <PlacesInput
      key={resolvedCountryCode || "unrestricted"}
      value={value}
      onChange={onChange}
      onPlaceSelect={onPlaceSelect}
      countryCode={resolvedCountryCode}
      placeholder={placeholder}
      className={className}
      id={id}
      name={name}
    />
  );
}

// Inner component – only mounted once window.google.maps is available
// Uses the new AutocompleteSuggestion API (replaces deprecated AutocompleteService)
function PlacesInput({
  value,
  onChange,
  onPlaceSelect,
  countryCode,
  placeholder,
  className,
  id,
  name,
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Sync parent-controlled value (e.g. form reset)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInputValue(value || "");
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = async (input) => {
    if (!input || input.length < 2) {
      setSuggestions([]);
      return;
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
    try {
      const request = {
        input,
        sessionToken: sessionTokenRef.current,
        ...(countryCode && { includedRegionCodes: [countryCode] }),
      };
      const { suggestions: results } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request,
        );
      setSuggestions(results || []);
    } catch (err) {
      console.error("Places autocomplete error:", err);
      setSuggestions([]);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange?.(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    const pred = suggestion.placePrediction;
    const text = pred.text.text;
    setInputValue(text);
    setSuggestions([]);
    onChange?.(text);
    sessionTokenRef.current = null; // reset session after selection

    if (!onPlaceSelect) return;

    try {
      const place = pred.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const comps = place.addressComponents || [];

      const get = (type) =>
        comps.find((c) => c.types.includes(type))?.longText || "";

      const streetNumber = get("street_number");
      const streetName = get("route");
      const street = streetNumber
        ? `${streetNumber} ${streetName}`
        : streetName;

      const city =
        get("locality") ||
        get("sublocality_level_1") ||
        get("postal_town") ||
        get("administrative_area_level_2");

      onPlaceSelect({
        address: street || text,
        city,
        state: get("administrative_area_level_1"),
        country: get("country"),
        postcode: get("postal_code"),
      });
    } catch (err) {
      console.error("Place details error:", err);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInput}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((s, i) => {
            const pred = s.placePrediction;
            const mainText = pred.mainText?.text || pred.text.text;
            const secondaryText = pred.secondaryText?.text || "";
            return (
              <li
                key={i}
                className="px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(s);
                }}
              >
                <span className="font-medium">{mainText}</span>
                {secondaryText && (
                  <span className="text-muted-foreground ml-1 text-xs">
                    {secondaryText}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
